/**
 * @fileoverview Profitability page — Net Profit 5-year trend.
 * @description Ported from the InsightBox Banking design system's own
 * `Profitability/NetProfit5Y.tsx`. "5 Years" plots the FY21→FY25 trend; an individual year drills
 * into that year's own Apr→Mar monthly glide instead of a single lonely point (see
 * `monthlySeries`). Page-local `fy`/`branchId` filter state replaces the reference's global
 * FilterContext, per the Banking module plan's "explicit deviations".
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FISCAL_YEARS,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  TrendArea,
  branchLabel,
  core,
  fiveYearGrowth,
  formatCr,
  formatSignedPct,
  monthlySeries,
  peakYear,
  prevFY,
  resolveBankingAccessScope,
  resolveFy,
  sumAcrossYears,
  troughYear,
  useBankingData,
  yoyGrowth,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'

/**
 * @component
 */
function BankingProfitabilityPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.profitability.kicker')}
      title={t('banking.profitability.title')}
      description={t('banking.profitability.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const is5Years = fyFilter === FIVE_YEAR_ID
  const fy = resolveFy(fyFilter)
  const selector = (f: (typeof FISCAL_YEARS)[number]) => core(data, f, branchId).netProfit

  const current = selector(fy)
  const yoy = yoyGrowth(fy, selector)
  const priorFy = prevFY(fy)
  const priorValue = priorFy ? selector(priorFy) : null
  const fiveYear = fiveYearGrowth(selector)
  const peak = peakYear(selector)
  const trough = troughYear(selector)
  const sumProfit = sumAcrossYears(selector)

  const chartData = is5Years
    ? FISCAL_YEARS.map((f) => ({ fy: f, value: selector(f), growthPct: yoyGrowth(f, selector) }))
    : monthlySeries(data, fy, selector)

  const kpis: BankingKpiStripItem[] = is5Years
    ? [
        {
          label: t('banking.profitability.kpi.fiveYearSum'),
          value: formatCr(sumProfit),
          sublabel: t('banking.profitability.kpi.fiveYearSumSublabel'),
          highlight: true,
        },
        {
          label: t('banking.profitability.kpi.latestYear', { fy }),
          value: formatCr(current),
          sublabel: t('banking.profitability.kpi.latestYearSublabel'),
        },
        {
          label: t('banking.profitability.kpi.fiveYearGrowth'),
          value: formatSignedPct(fiveYear),
          sublabel: t('banking.profitability.kpi.fiveYearGrowthSublabel'),
        },
        {
          label: t('banking.profitability.kpi.peak'),
          value: formatCr(peak.value),
          sublabel: peak.fy,
        },
      ]
    : [
        {
          label: t('banking.profitability.kpi.netProfit'),
          value: formatCr(current),
          sublabel: fy,
          highlight: true,
        },
        {
          label: t('banking.profitability.kpi.priorYear'),
          value: priorValue !== null ? formatCr(priorValue) : '—',
          sublabel: priorFy ?? t('banking.profitability.kpi.noPriorYear'),
        },
        {
          label: t('banking.profitability.kpi.yoyGrowth'),
          value: yoy !== null ? formatSignedPct(yoy) : '—',
          sublabel: yoy !== null ? t('banking.profitability.kpi.vsPriorYear') : '',
        },
        {
          label: t('banking.profitability.kpi.fiveYearPeak'),
          value: formatCr(peak.value),
          sublabel: peak.fy,
        },
      ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={
          is5Years
            ? t('banking.profitability.chartTitleFiveYear')
            : t('banking.profitability.chartTitleMonthly', { fy })
        }
        subtitle={
          is5Years
            ? t('banking.profitability.chartSubtitleFiveYear', { branch: branchLabel(branchId) })
            : t('banking.profitability.chartSubtitleMonthly', { branch: branchLabel(branchId) })
        }
      >
        <div className="relative min-h-0 flex-1">
          {/* Keyed on the view mode so switching between the 5-point yearly series and the
              12-point monthly series remounts the chart fresh instead of recharts trying to
              animate a transition between two differently-shaped datasets — see TrendArea's own
              file comment for why that mismatch used to crash the whole app. */}
          <TrendArea
            key={is5Years ? '5y' : 'monthly'}
            data={chartData}
            formatValue={(v) => formatCr(v)}
            height="100%"
            colorByTrend
          />
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummary')}
        lines={
          is5Years
            ? [
                t('banking.profitability.summary.fiveYearLine1', {
                  current: formatCr(current),
                  fy,
                  yoyClause:
                    yoy !== null
                      ? t('banking.profitability.summary.yoyClause', {
                          value: formatSignedPct(yoy),
                        })
                      : '',
                  sum: formatCr(sumProfit),
                }),
                t('banking.profitability.summary.fiveYearLine2', {
                  troughValue: formatCr(trough.value),
                  troughFy: trough.fy,
                  peakValue: formatCr(peak.value),
                  peakFy: peak.fy,
                  growth: formatSignedPct(fiveYear),
                }),
              ]
            : [
                t('banking.profitability.summary.yearLine1', {
                  current: formatCr(current),
                  fy,
                  yoyClause:
                    yoy !== null
                      ? t('banking.profitability.summary.yoyClauseYear', {
                          direction: yoy >= 0 ? t('banking.up') : t('banking.down'),
                          value: formatSignedPct(yoy),
                          priorFy,
                        })
                      : '',
                }),
                t('banking.profitability.summary.yearLine2'),
              ]
        }
      />
    </div>
  )
}

export default BankingProfitabilityPage
